import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
	Card,
	CardContent,
	Typography,
	Button,
	Box,
	Chip,
	Divider,
	useTheme,
	IconButton,
	Avatar,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
} from '@mui/material';
import {
	Radar,
	RadarChart,
	PolarGrid,
	PolarAngleAxis,
	ResponsiveContainer,
} from 'recharts';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import PersonIcon from '@mui/icons-material/Person';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import EventIcon from '@mui/icons-material/Event';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { format } from 'date-fns';
import { fi } from 'date-fns/locale';
import chatSessionService from '../../../services/chatSessionService';

const TalentCard = ({
	talent,
	match,
	isSelected,
	onSelect = () => {
		/* Optional callback */
	},
	showInterviewButton,
	interviewSlots = [],
	isAuthenticated,
	sessionId,
	onInterestUpdate = () => {
		/* Optional callback */
	},
	mode = 'chat', // 'chat' or 'sidebar'
}) => {
	if (!talent) {
		console.error('TalentCard: talent prop is missing');
		return null;
	}

	const [showSlots, setShowSlots] = useState(false);
	const [isInterestDialogOpen, setIsInterestDialogOpen] = useState(false);
	const [notes, setNotes] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showAllSkills, setShowAllSkills] = useState(false);

	const theme = useTheme();

	const {
		matchScore,
		skillMatch,
		roleMatch,
		availabilityScore,
		vectorScore,
	} = useMemo(() => {
		const score = (match?.score || 0) * 100;
		const details = match?.matchDetails || {};

		return {
			matchScore: score,
			skillMatch: details.skillMatchPercentage || 0,
			roleMatch: (details.roleMatchPercentage || 0) / 40,
			availabilityScore: (details.availabilityScore || 0) * 100,
			vectorScore: (details.vectorScore || 0) * 100,
		};
	}, [match]);

	const teamRoleData = useMemo(
		() =>
			Object.keys(talent.teamrole || {}).map((role) => ({
				subject: role.charAt(0).toUpperCase() + role.slice(1),
				A: talent.teamrole[role],
			})),
		[talent.teamrole],
	);

	const handleExpressInterest = async () => {
		if (!sessionId) {
			console.error('No session ID provided');
			return;
		}

		try {
			setIsSubmitting(true);
			const talentId = talent.id || talent._id;

			if (!talentId) {
				console.error('No talent ID found:', talent);
				return;
			}

			await chatSessionService.expressInterest(
				sessionId,
				talentId,
				notes.trim() || undefined,
			);
			setIsInterestDialogOpen(false);
			onInterestUpdate();
		} catch (error) {
			console.error('Error expressing interest:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Järjestä tiimiroolit suurimmasta pienimpään
	const topRoles = Object.entries(talent.teamrole || {})
		.sort(([, a], [, b]) => b - a)
		.slice(0, 3);
		
	// Parse skills for display
	const skillsArray = Object.entries(talent.skills || {}).map(
		([_, skill]) => skill.skill
	);
	
	const displaySkills = showAllSkills 
		? skillsArray 
		: skillsArray.slice(0, 5);
	
	const hiddenSkillsCount = skillsArray.length - displaySkills.length;

	// Determine if we should use the compact layout
	const isCompact = mode === 'sidebar';

	// Define handlers for toggling skill display
	const handleShowMoreSkills = () => {
		setShowAllSkills(true);
	};
	
	const handleShowLessSkills = () => {
		setShowAllSkills(false);
	};

	return (
		<Card
			sx={{
				mb: 2,
				position: 'relative',
				border: isSelected
					? `2px solid ${theme.palette.primary.main}`
					: 'none',
			}}
		>
			<CardContent>
				{/* Header */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						mb: 2,
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
						<Avatar
							alt={talent?.user?.name}
							src={talent?.profilePicture}
							sx={{ width: 56, height: 56 }}
						/>
						<Box>
							<Typography variant="h6" gutterBottom>
								{talent?.name || `Talentti ${talent.id}`}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{talent?.university}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{talent?.major}
							</Typography>
							<Button
								component={Link}
								to={`/app/talents/${talent.id}`}
								size="medium"
								variant="outlined"
								sx={{
									mt: 1,
									borderColor: theme.palette.primary.main,
									color: theme.palette.primary.main,
									'&:hover': {
										backgroundColor:
											'rgba(124, 85, 229, 0.04)',
										borderColor: '#7c55e5',
										color: '#7c55e5',
									},
								}}
								startIcon={<PersonSearchIcon />}
								target="_blank"
								rel="noopener noreferrer"
							>
								Katso koko profiili
							</Button>
						</Box>
					</Box>
					<Box sx={{ textAlign: 'right' }}>
						<Typography variant="h6" color="primary">
							{isAuthenticated
								? `${talent.hourlyRate || 40}€/h`
								: '40€/h'}
							{!isAuthenticated && (
								<Typography
									variant="caption"
									sx={{ display: 'block', opacity: 0.7 }}
								>
									(arvio)
								</Typography>
							)}
						</Typography>
					</Box>
				</Box>

				<Divider sx={{ my: 2 }} />

				{/* Skills and Team Role with responsive layout */}
				<Box
					sx={{
						display: 'flex',
						flexDirection: isCompact ? 'column' : { xs: 'column', md: 'row' },
						gap: 4,
					}}
				>
					{/* Skills */}
					<Box sx={{ flex: 1 }}>
						<Typography variant="subtitle1" gutterBottom>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 1,
								}}
							>
								<WorkspacePremiumIcon color="primary" />
								Taidot
							</Box>
						</Typography>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: 1,
								mb: { xs: 3, md: 0 },
							}}
						>
							{displaySkills.map((skill) => (
								<Chip
									key={skill}
									label={skill}
									size="small"
									sx={{
										backgroundColor:
											theme.palette.primary.light,
										color: theme.palette.primary
											.contrastText,
										'&:hover': {
											backgroundColor:
												theme.palette.primary.main,
										},
										fontWeight: 500,
										border: `1px solid ${theme.palette.primary.main}`,
									}}
								/>
							))}
							{hiddenSkillsCount > 0 && (
								<Button
									size="small"
									variant="text"
									onClick={showAllSkills ? handleShowLessSkills : handleShowMoreSkills}
									sx={{
										color: theme.palette.primary.main,
										ml: 1,
										fontSize: '0.75rem',
										textTransform: 'none',
										minWidth: 'auto',
										padding: '2px 8px',
										display: 'inline-flex',
										visibility: 'visible',
										'&:hover': {
											backgroundColor: 'rgba(124, 85, 229, 0.04)',
										},
									}}
								>
									{showAllSkills ? 'Näytä vähemmän' : `+${hiddenSkillsCount} lisää`}
								</Button>
							)}
						</Box>
					</Box>

					{/* Team Role Chart */}
					<Box sx={{ flex: 1 }}>
						<Typography variant="subtitle1" gutterBottom>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 1,
								}}
							>
								<PersonIcon color="primary" />
								Tiimirooli
							</Box>
						</Typography>
						<Box sx={{ height: isCompact ? 200 : 250, width: '100%', mt: 2 }}>
							<ResponsiveContainer>
								<RadarChart data={teamRoleData}>
									<PolarGrid />
									<PolarAngleAxis
										dataKey="subject"
										tick={{
											fill: theme.palette.text.secondary,
											fontSize: 12,
										}}
									/>
									<Radar
										name="Rooli"
										dataKey="A"
										stroke={theme.palette.primary.main}
										fill={theme.palette.primary.main}
										fillOpacity={0.6}
									/>
								</RadarChart>
							</ResponsiveContainer>
						</Box>
					</Box>
				</Box>

				{/* {match && (
					<>
						<Box sx={{ mb: 2 }}>
							<Typography variant="subtitle1" gutterBottom>
								Sopivuus tehtävään: {matchScore.toFixed(1)}%
							</Typography>
							<LinearProgress
								variant="determinate"
								value={matchScore}
								sx={{ height: 10, borderRadius: 5 }}
							/>
						</Box>

						<Box sx={{ mb: 2 }}>
							<Typography variant="subtitle2" gutterBottom>
								Sopivuuden yksityiskohdat:
							</Typography>
							<Box
								sx={{
									display: 'flex',
									gap: 1,
									flexWrap: 'wrap',
								}}
							>
								<Tooltip title="Taitojen vastaavuus projektin vaatimuksiin">
									<Chip
										label={`Taidot: ${skillMatch.toFixed(1)}%`}
										color="primary"
										variant="outlined"
										size="small"
									/>
								</Tooltip>
								<Tooltip title="Tiimiroolin sopivuus">
									<Chip
										label={`Tiimirooli: ${roleMatch.toFixed(1)}%`}
										color="primary"
										variant="outlined"
										size="small"
									/>
								</Tooltip>
								<Tooltip title="Saatavuus projektin aikana">
									<Chip
										label={`Saatavuus: ${availabilityScore.toFixed(1)}%`}
										color="primary"
										variant="outlined"
										size="small"
									/>
								</Tooltip>
								<Tooltip title="Tekoälyn arvio sopivuudesta">
									<Chip
										label={`AI Match: ${vectorScore.toFixed(1)}%`}
										color="primary"
										variant="outlined"
										size="small"
									/>
								</Tooltip>
							</Box>
						</Box>
					</>
				)} */}

				{/* Olemassa olevat tiedot */}
				<Typography variant="body2" color="text.secondary" gutterBottom>
					{talent.description}
				</Typography>

				{/* Action Button */}
				<Box
					sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
				>
					{showInterviewButton && (
						<Box sx={{ mt: 3, width: '100%' }}>
							{!showSlots ? (
								<Button
									variant="contained"
									color="primary"
									fullWidth
									onClick={() => setShowSlots(true)}
									startIcon={<EventIcon />}
								>
									Valitse haastatteluun
								</Button>
							) : (
								<>
									<Typography
										variant="subtitle2"
										gutterBottom
										sx={{ mb: 2 }}
									>
										Valitse sopiva haastatteluaika:
									</Typography>
									<Box
										sx={{
											display: 'flex',
											flexWrap: 'wrap',
											gap: 1,
										}}
									>
										{Array.isArray(interviewSlots) &&
										interviewSlots.length > 0 ? (
											interviewSlots.map((slot) => (
												<Chip
													key={slot.id}
													label={format(
														new Date(slot.time),
														'dd.MM. HH:mm',
														{ locale: fi },
													)}
													onClick={() =>
														onSelect(slot)
													}
													variant="outlined"
													color="primary"
													sx={{
														cursor: 'pointer',
														'&:hover': {
															backgroundColor:
																theme.palette
																	.primary
																	.light,
														},
													}}
												/>
											))
										) : (
											<Typography
												variant="body2"
												color="text.secondary"
											>
												Ei vapaita haastatteluaikoja
											</Typography>
										)}
									</Box>
									<Button
										size="small"
										sx={{ mt: 2 }}
										onClick={() => setShowSlots(false)}
									>
										Piilota ajat
									</Button>
								</>
							)}
						</Box>
					)}
				</Box>

				{/* Korvaa vanha yhteydenottonappi uudella tilausnapilla */}
				<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
					<Button
						variant="contained"
						color={match?.interested ? 'success' : 'primary'}
						fullWidth
						onClick={() =>
							!match?.interested && setIsInterestDialogOpen(true)
						}
						startIcon={<FavoriteIcon />}
						disabled={match?.interested || !sessionId}
						sx={{
							py: 1.5,
							fontSize: '1.1rem',
							fontWeight: 500,
							boxShadow: 2,
							'&:hover': {
								boxShadow: 4,
							},
						}}
					>
						{match?.interested
							? 'Tilaus lähetetty'
							: 'Tilaa asiantuntija'}
					</Button>
				</Box>

				{/* Kiinnostuksen ilmaisemisen dialogi */}
				<Dialog
					open={isInterestDialogOpen}
					onClose={() => setIsInterestDialogOpen(false)}
					maxWidth="sm"
					fullWidth
				>
					<DialogTitle>
						Tilaa asiantuntija - {talent?.name}
					</DialogTitle>
					<DialogContent>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ mb: 2 }}
						>
							Voit halutessasi jättää lisätietoja tilaukseen
							liittyen. Olemme sinuun yhteydessä mahdollisimman
							pian.
						</Typography>
						<TextField
							fullWidth
							multiline
							rows={4}
							variant="outlined"
							placeholder="Kirjoita lisätietoja (vapaaehtoinen)"
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							inputProps={{ maxLength: 1000 }}
						/>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => setIsInterestDialogOpen(false)}
							disabled={isSubmitting}
						>
							Peruuta
						</Button>
						<Button
							onClick={handleExpressInterest}
							variant="contained"
							disabled={isSubmitting}
						>
							{isSubmitting ? 'Lähetetään...' : 'Lähetä'}
						</Button>
					</DialogActions>
				</Dialog>
			</CardContent>
		</Card>
	);
};

export default React.memo(TalentCard, (prevProps, nextProps) => {
	return (
		prevProps.talent._id === nextProps.talent._id &&
		prevProps.match.score === nextProps.match.score &&
		prevProps.isSelected === nextProps.isSelected &&
		prevProps.match?.interested === nextProps.match?.interested
	);
});
